import React, { useState } from 'react';
import { InventoryAdminListItem, InventoryScheduleRequest } from '../../types';
import { apiService } from '../../services/api';

interface ScheduleInventoryFormProps {
  inventory: InventoryAdminListItem;
  onSubmit: () => void;
  onCancel: () => void;
  loading: boolean;
}

export const ScheduleInventoryForm: React.FC<ScheduleInventoryFormProps> = ({
  inventory,
  onSubmit,
  onCancel,
  loading,
}) => {
  const [formData, setFormData] = useState<InventoryScheduleRequest>({
    startTime: 0,
    displayTime: '',
    maxSlots: 30,
    partialPaymentAllowed: false,
    partialPaymentAmount: 0,
    partialPaymentTill: 0,
    price: 0,
    subText: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.displayTime.trim()) {
      newErrors.displayTime = 'Display time is required';
    }
    
    if (formData.startTime <= 0) {
      newErrors.startTime = 'Start time is required';
    }
    
    if (formData.maxSlots <= 0) {
      newErrors.maxSlots = 'Max slots must be greater than 0';
    }
    
    if (formData.price <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }
    
    if (formData.partialPaymentAllowed && formData.partialPaymentAmount <= 0) {
      newErrors.partialPaymentAmount = 'Partial payment amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      await apiService.scheduleInventory(inventory.id, formData);
      onSubmit();
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to schedule inventory' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof InventoryScheduleRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleDateTimeChange = (dateTimeString: string) => {
    const timestamp = new Date(dateTimeString).getTime();
    handleInputChange('startTime', timestamp);
    
    // Auto-generate display time
    const date = new Date(dateTimeString);
    const displayTime = date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    handleInputChange('displayTime', displayTime);
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Schedule Inventory</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Create a new schedule for: <span className="font-medium">{inventory.title}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Schedule Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Schedule Details</h3>
            
            <div>
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date & Time *
              </label>
              <input
                type="datetime-local"
                id="startTime"
                min={getCurrentDateTime()}
                onChange={(e) => handleDateTimeChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.startTime ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={loading || isSubmitting}
              />
              {errors.startTime && (
                <p className="mt-1 text-sm text-red-600">{errors.startTime}</p>
              )}
            </div>

            <div>
              <label htmlFor="displayTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Display Time *
              </label>
              <input
                type="text"
                id="displayTime"
                value={formData.displayTime}
                onChange={(e) => handleInputChange('displayTime', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.displayTime ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Dec 25, 2024, 06:00 AM"
                disabled={loading || isSubmitting}
              />
              {errors.displayTime && (
                <p className="mt-1 text-sm text-red-600">{errors.displayTime}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="maxSlots" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Slots *
                </label>
                <input
                  type="number"
                  id="maxSlots"
                  value={formData.maxSlots}
                  onChange={(e) => handleInputChange('maxSlots', parseInt(e.target.value))}
                  min="1"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    errors.maxSlots ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading || isSubmitting}
                />
                {errors.maxSlots && (
                  <p className="mt-1 text-sm text-red-600">{errors.maxSlots}</p>
                )}
              </div>

              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price (₹) *
                </label>
                <input
                  type="number"
                  id="price"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', parseInt(e.target.value))}
                  min="1"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    errors.price ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading || isSubmitting}
                />
                {errors.price && (
                  <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                )}
              </div>
            </div>
          </div>

          {/* Payment Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Payment Options</h3>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.partialPaymentAllowed}
                  onChange={(e) => handleInputChange('partialPaymentAllowed', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={loading || isSubmitting}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Allow Partial Payment</span>
              </label>
            </div>

            {formData.partialPaymentAllowed && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="partialPaymentAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Partial Payment Amount (₹)
                  </label>
                  <input
                    type="number"
                    id="partialPaymentAmount"
                    value={formData.partialPaymentAmount}
                    onChange={(e) => handleInputChange('partialPaymentAmount', parseInt(e.target.value))}
                    min="1"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      errors.partialPaymentAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading || isSubmitting}
                  />
                  {errors.partialPaymentAmount && (
                    <p className="mt-1 text-sm text-red-600">{errors.partialPaymentAmount}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="partialPaymentTill" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Partial Payment Till (Hours before trip)
                  </label>
                  <input
                    type="number"
                    id="partialPaymentTill"
                    value={formData.partialPaymentTill}
                    onChange={(e) => handleInputChange('partialPaymentTill', parseInt(e.target.value))}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    disabled={loading || isSubmitting}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Additional Information</h3>
            
            <div>
              <label htmlFor="subText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sub Text
              </label>
              <input
                type="text"
                id="subText"
                value={formData.subText}
                onChange={(e) => handleInputChange('subText', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., 2 Sharing AC Room @ ₹4999"
                disabled={loading || isSubmitting}
              />
              <p className="mt-1 text-sm text-gray-500">
                Optional: Additional pricing or room details
              </p>
            </div>
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading || isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Scheduling...
                </div>
              ) : (
                'Schedule Inventory'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
